<div class="cd-page">
  <app-counselor-nav></app-counselor-nav>

  <main class="cd-main">
    <!-- ── Header ───────────────────────────────────────────────── -->
    <div class="cd-header">
      <div>
        <div class="cd-title">Audit Logs</div>
        <div class="cd-subtitle">FERPA &amp; COPPA compliance activity trail</div>
      </div>

      @if (!loading) {
        <div class="al-total" aria-live="polite" aria-atomic="true">{{ total }} total records</div>
      }
    </div>

    <!-- ── Filters ──────────────────────────────────────────────── -->
    <div class="cd-card cd-filter-bar" role="region" aria-label="Audit log filters">
      <!-- Event type -->
      <mat-form-field appearance="outline" class="al-mat-field">
        <mat-label>Event Type</mat-label>
        <mat-select [(ngModel)]="searchEventType">
          <mat-option value="">All types</mat-option>
          @for (et of EVENT_TYPES; track et) {
            <mat-option [value]="et">{{ et }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <!-- Status -->
      <mat-form-field appearance="outline" class="al-mat-field">
        <mat-label>Status</mat-label>
        <mat-select [(ngModel)]="filterSuccess">
          <mat-option value="">All</mat-option>
          <mat-option value="true">Success</mat-option>
          <mat-option value="false">Failed</mat-option>
        </mat-select>
      </mat-form-field>

      <!-- From date -->
      <mat-form-field appearance="outline" class="al-mat-field">
        <mat-label>From</mat-label>
        <input matInput [matDatepicker]="fromPicker" [(ngModel)]="fromDate" />
        <mat-datepicker-toggle matIconSuffix [for]="fromPicker" />
        <mat-datepicker #fromPicker />
      </mat-form-field>

      <!-- To date -->
      <mat-form-field appearance="outline" class="al-mat-field">
        <mat-label>To</mat-label>
        <input matInput [matDatepicker]="toPicker" [(ngModel)]="toDate" />
        <mat-datepicker-toggle matIconSuffix [for]="toPicker" />
        <mat-datepicker #toPicker />
      </mat-form-field>

      <!-- Actions -->
      <div class="al-filter-actions" role="group" aria-label="Filter actions">
        <button class="cd-btn-primary" (click)="applyFilters()">Apply</button>
        <button class="cd-link-btn" (click)="clearFilters()">Clear</button>
      </div>
    </div>

    <!-- ── Error ─────────────────────────────────────────────────── -->
    @if (error) {
      <div class="al-error" role="alert" aria-live="assertive">
        {{ error }}
      </div>
    }

    <!-- ── Loading ───────────────────────────────────────────────── -->
    @if (loading) {
      <div class="al-loading" role="status" aria-live="polite" aria-atomic="true">
        <mat-spinner diameter="32" aria-hidden="true" />
      </div>
    }

    <!-- ── Table ─────────────────────────────────────────────────── -->
    @if (!loading && !error) {
      <div class="cd-card al-table-card">
        <div class="cd-card-header">
          <h3>Activity Log</h3>
          <span class="al-page-info" aria-live="polite" aria-atomic="true">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
        </div>

        <div class="al-table-wrap">
          <table
            mat-table
            [dataSource]="logs"
            class="al-mat-table"
            role="table"
            aria-label="Audit log table"
          >
            <!-- Timestamp -->
            <ng-container matColumnDef="timestamp">
              <th mat-header-cell *matHeaderCellDef>Timestamp</th>
              <td mat-cell *matCellDef="let log" class="al-ts">
                {{ formatTimestamp(log.timestamp) }}
              </td>
            </ng-container>

            <!-- Event Type -->
            <ng-container matColumnDef="eventType">
              <th mat-header-cell *matHeaderCellDef>Event Type</th>
              <td mat-cell *matCellDef="let log">
                <span
                  class="al-event-badge"
                  [class]="'al-event-' + (log.eventType?.toLowerCase() ?? 'default')"
                  [attr.aria-label]="'Event type ' + (log.eventType ?? 'unknown')"
                >
                  {{ log.eventType ?? '—' }}
                </span>
              </td>
            </ng-container>

            <!-- Action -->
            <ng-container matColumnDef="action">
              <th mat-header-cell *matHeaderCellDef>Action</th>
              <td mat-cell *matCellDef="let log" class="al-action">
                {{ log.action ?? '—' }}
              </td>
            </ng-container>

            <!-- Performed By -->
            <ng-container matColumnDef="performedBy">
              <th mat-header-cell *matHeaderCellDef>Performed By</th>
              <td mat-cell *matCellDef="let log">
                {{ performedBy(log) }}
              </td>
            </ng-container>

            <!-- Student ID -->
            <ng-container matColumnDef="studentId">
              <th mat-header-cell *matHeaderCellDef>Student ID</th>
              <td mat-cell *matCellDef="let log">
                {{ log.studentId ?? '—' }}
              </td>
            </ng-container>

            <!-- Reason -->
            <ng-container matColumnDef="reason">
              <th mat-header-cell *matHeaderCellDef>Reason</th>
              <td mat-cell *matCellDef="let log" class="al-reason">
                {{ log.reason ?? '—' }}
              </td>
            </ng-container>

            <!-- Disclosed To -->
            <ng-container matColumnDef="disclosedTo">
              <th mat-header-cell *matHeaderCellDef>Disclosed To</th>
              <td mat-cell *matCellDef="let log">
                {{ log.disclosedTo ?? '—' }}
              </td>
            </ng-container>

            <!-- Consent -->
            <ng-container matColumnDef="consent">
              <th mat-header-cell *matHeaderCellDef>Consent</th>
              <td mat-cell *matCellDef="let log">
                @if (log.consentVerified !== null) {
                  <span
                    class="cd-badge"
                    [class]="log.consentVerified ? 'on-track' : 'needs-attn'"
                    [attr.aria-label]="
                      'Consent ' + (log.consentVerified ? 'verified' : 'not verified')
                    "
                  >
                    {{ log.consentVerified ? 'Yes' : 'No' }}
                  </span>
                } @else {
                  <span aria-hidden="true">—</span>
                }
              </td>
            </ng-container>

            <!-- Status -->
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let log">
                <span
                  class="cd-badge"
                  [class]="log.success ? 'on-track' : 'needs-attn'"
                  [attr.aria-label]="'Status ' + successLabel(log)"
                >
                  {{ successLabel(log) }}
                </span>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns" class="al-row"></tr>

            <!-- Empty state -->
            <tr class="mat-row" *matNoDataRow>
              <td
                class="al-empty"
                [attr.colspan]="displayedColumns.length"
                role="status"
                aria-live="polite"
              >
                No logs found for the selected filters.
              </td>
            </tr>
          </table>
        </div>

        <!-- ── Pagination ─────────────────────────────────────────── -->
        <div class="al-pagination" role="navigation" aria-label="Audit log pagination">
          <button
            class="cd-link-btn"
            (click)="prevPage()"
            [disabled]="(filters.offset ?? 0) === 0"
            aria-label="Previous page"
          >
            ← Prev
          </button>

          <span class="al-page-info" aria-live="polite" aria-atomic="true">
            {{ currentPage }} / {{ totalPages }}
          </span>

          <button
            class="cd-link-btn"
            (click)="nextPage()"
            [disabled]="currentPage >= totalPages"
            aria-label="Next page"
          >
            Next →
          </button>
        </div>
      </div>
    }
  </main>
</div>
